// Giao diện theo mùa trong khung app (GlassLayout / DashboardLayout). Dịp do admin chọn
// (lib/season/store.ts); nếu không có dịp, mọi thành phần ở đây không vẽ gì.
//   · SeasonLayer: hạt nền + cảnh nền (điện thoại) phía sau nội dung, và lớp bung hiệu ứng khi
//     tạo đơn. Gắn một lần trong khung.
//   · SeasonHello: ô "Đơn hôm nay" đầu trang Tổng quan (components/overview/parts.tsx · TodayHero) khoác áo
//     theo dịp — nền + cảnh động phía sau số liệu, không thêm chữ; luôn hiện suốt dịp, không có nút ẩn.
//   · SeasonDeco / SeasonFlag / SeasonHat / SeasonDockCap: trang trí khung.
//   · SeasonFxToggle: người dùng tự tắt / bật hiệu ứng động trên máy mình.

import { useEffect, useId, useMemo, useRef, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { Sparkles } from 'lucide-react'
import type { SeasonKey } from '@shoptrack/common/season'
import { cn } from '@/lib/utils'
import { useViewportMode } from '@/hooks/useViewportMode'
import { Engine, burst, drawFlag, setupStage } from '@/lib/season/engine'
import { addTicker } from '@/lib/season/runtime'
import { BACKDROP, CARD, DECO, HAT, HERO, SNOWCAP } from '@/lib/season/scenes'
import { onSeasonCelebrate, seasonMotion, setSeasonFx, useSeason } from '@/lib/season/store'
import { SEASONS } from '@/lib/season/themes'

const viewport = () => ({ w: document.documentElement.clientWidth, h: document.documentElement.clientHeight })

/** Dựng và chạy các canvas sân khấu (data-stage) bên trong `ref`; dựng lại khi đổi cỡ. */
function useStages(ref: RefObject<HTMLElement>, season: SeasonKey | null, motion: boolean, html: string) {
  useEffect(() => {
    const root = ref.current
    if (!root || !season || !html) return
    let engines: Engine[] = []
    const build = () => {
      engines = Array.from(root.querySelectorAll<HTMLCanvasElement>('canvas[data-stage]')).map((cv) => setupStage(cv, season, motion))
    }
    build()
    const ro = new ResizeObserver(() => {
      if (engines.some((e) => Math.abs(e.cv.offsetWidth - e.w) > 1 || Math.abs(e.cv.offsetHeight - e.h) > 1)) build()
    })
    ro.observe(root)
    const off = motion
      ? addTicker((dt) => {
          for (const e of engines) {
            e.step(dt)
            e.draw()
          }
        })
      : null
    return () => {
      ro.disconnect()
      off?.()
    }
  }, [ref, season, motion, html])
}

/** Điểm bung hiệu ứng: nút Tạo đơn đang hiện (data-season-origin), không có thì giữa đáy màn. */
function burstOrigin() {
  const { w, h } = viewport()
  for (const el of Array.from(document.querySelectorAll<HTMLElement>('[data-season-origin]'))) {
    const r = el.getBoundingClientRect()
    if (r.width && r.height) {
      const down = r.top < h / 2
      return { x: r.left + r.width / 2, y: r.top + r.height / 2 + (down ? 14 : -6), down }
    }
  }
  return { x: w / 2, y: h - 60, down: false }
}

export function SeasonLayer() {
  const st = useSeason()
  const { season } = st
  const motion = seasonMotion(st)
  const mobile = useViewportMode() === 'mobile'
  const fxRef = useRef<HTMLCanvasElement>(null)
  const burstRef = useRef<HTMLCanvasElement>(null)
  const backRef = useRef<HTMLDivElement>(null)
  const backHtml = useMemo(() => (season && mobile ? BACKDROP[season]?.() ?? '' : ''), [season, mobile])
  useStages(backRef, season, motion, backHtml)

  // Hạt nền: rải sẵn khắp màn; số hạt theo diện tích màn
  useEffect(() => {
    const cv = fxRef.current
    if (!cv || !season) return
    const e = new Engine(cv)
    const info = SEASONS[season]
    const fill = () => {
      const { w, h } = viewport()
      e.resize(w, h)
      const area = Math.min(2.4, Math.max(0.6, (w * h) / (390 * 820)))
      e.setAmbient(season, Math.round(info.particles * area), motion ? info.fireworks : 0, true)
      e.draw()
    }
    fill()
    let t = 0
    const onResize = () => {
      window.clearTimeout(t)
      t = window.setTimeout(fill, 150)
    }
    window.addEventListener('resize', onResize)
    const off = motion
      ? addTicker((dt) => {
          e.step(dt)
          e.draw()
        })
      : null
    return () => {
      window.removeEventListener('resize', onResize)
      window.clearTimeout(t)
      off?.()
    }
  }, [season, motion])

  // Bung hiệu ứng khi tạo đơn (celebrateSeason)
  useEffect(() => {
    const cv = burstRef.current
    if (!cv || !season || !motion) return
    const e = new Engine(cv)
    let off: (() => void) | null = null
    const stop = onSeasonCelebrate(() => {
      const { w, h } = viewport()
      if (Math.abs(e.w - w) > 1 || Math.abs(e.h - h) > 1) e.resize(w, h)
      const o = burstOrigin()
      burst(season, e, o.x, o.y, o.down)
      off ??= addTicker((dt) => {
        e.step(dt)
        e.draw()
        if (!e.p.length) {
          off?.()
          off = null
        }
      })
    })
    return () => {
      stop()
      off?.()
    }
  }, [season, motion])

  if (!season) return null
  return createPortal(
    <>
      {backHtml && <div ref={backRef} className="ss-backdrop" aria-hidden dangerouslySetInnerHTML={{ __html: backHtml }} />}
      <canvas ref={fxRef} className="ss-fx" aria-hidden />
      <canvas ref={burstRef} className="ss-burst" aria-hidden />
    </>,
    document.body,
  )
}

/**
 * Khung ô Hôm nay của Tổng quan: không có dịp thì là <section> thường; có dịp thì thêm nền theo dịp và
 * cảnh động phía sau nội dung (máy tính: cảnh rộng bên phải; điện thoại: cảnh nhỏ ở góc).
 * `slot` → data-slot của khối (glass.css bám vào để vẽ khối chào như app).
 */
export function SeasonHello({ className, slot, children }: { className?: string; slot?: string; children: ReactNode }) {
  const st = useSeason()
  const { season } = st
  const motion = seasonMotion(st)
  const phone = useViewportMode() === 'mobile'
  const ref = useRef<HTMLElement>(null)
  const sceneHtml = useMemo(() => (season ? (phone ? CARD : HERO)[season]?.() ?? '' : ''), [season, phone])
  useStages(ref, season, motion, sceneHtml)
  if (!season) return <section className={className} data-slot={slot}>{children}</section>
  return (
    <section ref={ref} data-slot={slot} className={cn(className, 'ss-hello', phone ? 'ss-card' : 'ss-hero', sceneHtml && 'has-scene')}>
      {sceneHtml && <div className="ss-scene" aria-hidden dangerouslySetInnerHTML={{ __html: sceneHtml }} />}
      {season === 'noel' && <span className="ss-abs" aria-hidden dangerouslySetInnerHTML={{ __html: SNOWCAP }} />}
      {children}
    </section>
  )
}

/** Đèn lồng / dây đèn / nhện… treo dưới thanh trên. Thanh trên phải là phần tử có định vị. */
export function SeasonDeco({ variant }: { variant: 'glass' | 'desktop' }) {
  const { season } = useSeason()
  const id = useId().replace(/[^a-zA-Z0-9]/g, '')
  const html = useMemo(() => (season ? DECO[variant][season]?.(id) ?? '' : ''), [season, variant, id])
  if (!html) return null
  return <div className="ss-deco" aria-hidden dangerouslySetInnerHTML={{ __html: html }} />
}

/** Lá cờ đỏ sao vàng tung bay — chỉ dịp 30/4. */
export function SeasonFlag() {
  const st = useSeason()
  const motion = seasonMotion(st)
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const cv = ref.current
    if (!cv) return
    drawFlag(cv, 0)
    if (!motion) return
    return addTicker((_, s) => drawFlag(cv, s))
  }, [st.season, motion])
  if (st.season !== 'thongnhat') return null
  return <canvas ref={ref} width={92} height={68} className="ss-flag" aria-hidden />
}

/** Mũ Noel trên ảnh đại diện (phần tử cha phải có định vị). */
export function SeasonHat() {
  const { season } = useSeason()
  if (season !== 'noel') return null
  return <span className="ss-abs" aria-hidden dangerouslySetInnerHTML={{ __html: HAT }} />
}

/** Tuyết phủ trên mép thanh điều hướng đáy. */
export function SeasonDockCap() {
  const { season } = useSeason()
  if (season !== 'noel') return null
  return <span className="ss-abs" aria-hidden dangerouslySetInnerHTML={{ __html: SNOWCAP }} />
}

export function SeasonFxToggle({ className, onDone }: { className?: string; onDone?: () => void }) {
  const st = useSeason()
  if (!st.season) return null
  return (
    <button
      type="button"
      aria-pressed={st.fx}
      onClick={() => {
        setSeasonFx(!st.fx)
        onDone?.()
      }}
      className={className}
    >
      <Sparkles className="size-4 shrink-0" aria-hidden />
      {st.fx ? 'Tắt hiệu ứng theo mùa' : 'Bật hiệu ứng theo mùa'}
    </button>
  )
}
